import { useState } from "react"
import {
  TABLE_PAGE_SIZE,
  DataTable,
  selectColumn,
  createSortableColumn,
  createCustomColumn,
  createActionsColumn,
  formatDateToDutch,
  AbbreviateTableColumn,
  getFullName,
  projectTypeMap,
  cn,
  mapClassroomsToNames,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  createFileRoute,
  useRouter,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { Intake, IntakeStatusMap } from "@/api/types/intake"
import {
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  useExportMutation,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { intakeQueryKeys } from "@/api/query-keys/intakes"
import {
  INTAKE_STORAGE_KEY,
  useIntakeFilters,
} from "@/hooks/filters/useIntakeFilters"
import EditIntakeModal from "@/components/modals/intake/EditIntakeModal"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import DeleteIntakeModal from "@/components/modals/intake/DeleteIntakeModal"

export const Route = createFileRoute("/beheer/intakes/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "intake"),
  validateSearch: createTableSearchSchema(),
})

const DEFAULT_COLUMN_VISIBILITY = {
  maxStudents: false,
}

const ALL_COLUMN_IDS = [
  "id",
  "locationName",
  "classrooms",
  "date",
  "intakers",
  "projectTypes",
  "studentsCount",
  "maxStudents",
  "status",
]

function RouteComponent() {
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<Intake | null>(null)
  const router = useRouter()

  const searchParams = useSearch({ from: "/beheer/intakes/" })
  const navigate = useNavigate({
    from: "/beheer/intakes/",
  })

  const { filterItems, activeFilters, queryFn } = useIntakeFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
    queryKeyMerged,
  } = usePaginatedTableWithUrlState({
    queryKey: [...intakeQueryKeys.all, activeFilters],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: INTAKE_STORAGE_KEY,
    defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const handleNavigateDetail = (intakeId: Intake["id"]) => {
    router.navigate({
      to: "/beheer/intakes/$intakeId",
      params: { intakeId: intakeId.toString() },
      search: { from: router.state.location.href },
    })
  }

  const columns: ColumnDef<Intake>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createSortableColumn({
      accessorKey: "locationName",
      name: "Locatie",
    }),
    createCustomColumn({
      id: "classrooms",
      name: "Lokaal",
      cell: ({ row }) => (
        <span>{mapClassroomsToNames(row.original.classrooms)}</span>
      ),
      enableSorting: false,
    }),
    createCustomColumn({
      id: "date",
      name: "Intakedatum",
      cell: ({ row }) => (
        <span>
          {formatDateToDutch(row.original.date, { format: "dateWithWeekday" })}{" "}
          {`${row.original.startTime} - ${row.original.endTime}`}
        </span>
      ),
      accessorKey: "date",
    }),
    createCustomColumn({
      id: "intakers",
      name: "Intaker(s)",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={row.original.intakers.map((intaker) => ({
            id: intaker.id,
            name: getFullName({
              firstName: intaker.firstName,
              lastName: intaker.lastName,
            }),
          }))}
        />
      ),
      accessorKey: "intakers",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "projectTypes",
      name: "Traject",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={row.original.projectTypes.map((pt) => ({
            id: pt,
            name: projectTypeMap[pt],
          }))}
        />
      ),
      accessorKey: "projectTypes",
    }),
    createCustomColumn({
      id: "studentsCount",
      name: "Aantal cursisten",
      cell: ({ row }) => (
        <span>{`${row.original.studentsCount ?? 0} Cursisten`}</span>
      ),
      accessorKey: "studentsCount",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "maxStudents",
      name: "Max cursisten",
      cell: ({ row }) => <span>{row.original.maxStudents}</span>,
      accessorKey: "maxStudents",
    }),
    createCustomColumn({
      id: "status",
      name: "Status",
      cell: ({ row }) => (
        <span
          className={cn("font-bold", {
            "text-grey": row.original.status === "completed",
            "text-primary-green": row.original.status === "add_results",
            "text-primary-dark-blue": row.original.status === "scheduled",
          })}
        >
          {IntakeStatusMap[row.original.status]}
        </span>
      ),
      accessorKey: "status",
    }),
    createActionsColumn([
      {
        label: "Intake wijzigen",
        onClick: (e, intake) => {
          e.stopPropagation()
          setActiveRow(intake)
          setIsEditOpen(true)
        },
        hidden: (intake) => intake.status !== "scheduled",
      },
      {
        label: "Intake verwijderen",
        onClick: (e, intake) => {
          e.stopPropagation()
          setActiveRow(intake)
          setIsDeleteOpen(true)
        },
        hidden: (intake) =>
          intake.status !== "scheduled" || intake.studentsCount !== 0,
      },
    ]),
  ]

  const { mutate: exportIntakes } = useExportMutation({
    exportApiPrefix: "intakes",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Intakes"
        data={data?.data?.intakes || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        onRowClick={handleNavigateDetail}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        headerLeftActions={<MultiLevelDropdownMenu items={filterItems} />}
        headerRightActions={
          <>
            <TableHeaderActions
              exportAction={{
                onClick: exportIntakes,
              }}
            />

            <TableHeaderActions
              createAction={{
                label: "Nieuwe intake",
                to: "/beheer/intakes/aanmaken",
                entity: "intake",
                permissions: "create",
              }}
            />
          </>
        }
      />

      {isDeleteOpen && activeRow && (
        <DeleteIntakeModal
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          intake={activeRow}
          queryKey={queryKeyMerged}
        />
      )}

      <EditIntakeModal
        isEditOpen={isEditOpen}
        setIsEditOpen={setIsEditOpen}
        activeRow={activeRow}
        closeDialog={() => {
          setIsEditOpen(false)
          setActiveRow(null)
        }}
      />
    </ScreenWrapper>
  )
}
